import { useCallback, useEffect, useState } from 'react';
import { useAddress, useContract } from '@thirdweb-dev/react';
import { useAsync, useAsyncFn } from 'react-use';
import {
  bigNumberToDecimal,
  numberToBigNumberFixed
} from '@app/helpers/format';
import { useConfig } from '@app/config';
import { formatUnits } from 'ethers/lib/utils';

export function useConvertTokens() {
  const config = useConfig();
  const address = useAddress();
  const [state, setState] = useState<{
    amount: string;
  }>({
    amount: ''
  });

  const handleAmountChange = useCallback((val: string) => {
    setState(prev => ({
      ...prev,
      amount: val
    }));
  }, []);

  const { contract: kimContract } = useContract(config?.CONTRACTS.KIM);
  const { contract: xKimContract } = useContract(config?.CONTRACTS.X_KIM);

  const { value, loading: loadingTokenBalance } = useAsync(async () => {
    if (!kimContract || !address) {
      return;
    }

    const kimTokenBalance = await kimContract.call('balanceOf', [address]);

    return {
      myRyoBalance: formatUnits(kimTokenBalance, 18)
    };
  }, [kimContract, address]);

  const [{ loading: converting }, convert] = useAsyncFn(async () => {
    if (!kimContract || !address) {
      return;
    }

    return xKimContract?.call('convert', [
      numberToBigNumberFixed(+state.amount, 18)
    ]);
  }, [xKimContract, address, state.amount]);

  const resetStateValues = useCallback(() => {
    setState(prev => ({
      amount: ''
    }));
  }, []);

  return {
    state,
    handleAmountChange,
    myRyoBalance: value?.myRyoBalance,
    convert,
    converting,
    loadingTokenBalance,
    resetStateValues
  };
}

export function useKimTokenApprove(amount: string) {
  const address = useAddress();
  const config = useConfig();

  const { contract } = useContract(config?.CONTRACTS.KIM);

  const [{ value, loading: isFetchingAllowance }, getAllowance] =
    useAsyncFn(async () => {
      if (!contract || !address) {
        return;
      }

      try {
        const res = await contract.call('allowance', [
          address,
          config?.CONTRACTS.X_KIM
        ]);

        return bigNumberToDecimal(res, 18);
      } catch (e) {
        // do nothing
      }
    }, [contract, address, config]);

  const [{ loading }, approve] = useAsyncFn(async () => {
    if (value === undefined || Number(amount) <= value || !contract) {
      return;
    }

    const valueToApprove = Number(amount);

    return contract?.call('approve', [
      config?.CONTRACTS.X_KIM ?? '',
      numberToBigNumberFixed(valueToApprove, 18)
    ]);
  }, [contract, amount, value, config]);

  useEffect(() => {
    getAllowance();
  }, [getAllowance]);

  return {
    requiresApproval:
      !isFetchingAllowance && value !== undefined && Number(amount) > value,
    approve,
    isApproveInProgress: loading,
    getAllowance
  };
}
